import { CSSProperties } from 'react';
import type { ThemeColors } from '../../components/uchat/UCIndependentChat/hooks/useTheme';

export interface UCCategoryPrevewStyles {
  container: CSSProperties;
  contentContainer: CSSProperties;
  mainContainer: CSSProperties; // 主容器，带发光边框
  categoryGrid: CSSProperties;
  title: CSSProperties;
  subtitle: CSSProperties;
  cardWrapper: (params: { isSelected: boolean; color: string; isDark: boolean }) => CSSProperties;
  cardOuterBorder: (params: { isSelected: boolean; color: string; isDark: boolean }) => CSSProperties;
  cardInnerBorder: (params: { isSelected: boolean; color: string; isDark: boolean }) => CSSProperties;
  categoryCard: (params: { isSelected: boolean; color: string }) => CSSProperties;
  cardBody: (xs: boolean) => CSSProperties;
  icon: (xs: boolean, color: string) => CSSProperties;
  cardTitle: (xs: boolean, isSelected: boolean, color: string) => CSSProperties;
  cardDesc: (xs: boolean) => CSSProperties;
}

export const createUCCategoryPrevewStyles = (screens: any, colors: ThemeColors): UCCategoryPrevewStyles => ({
  container: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  contentContainer: {
    width: "100%",
    background: "transparent",
    padding: 0,
    margin: "0 auto",
  },
  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    columnGap: screens.xs ? "10px" : "12px",
    rowGap: screens.xs ? "18px" : "20px",
    width: "100%",
  },
  mainContainer: {
    width: "100%",
    borderRadius: "12px",
    padding: screens.xs ? "12px" : "16px",
    background: colors.background,
    // 暗色模式下添加浅绿色发光边框
    ...(colors.background === '#14172B' ? {
      border: "1px solid #52c41a",
      boxShadow: "0 0 20px rgba(82, 196, 26, 0.3)",
    } : {
      // 亮色模式下添加普通边框
      border: `1px solid ${colors.border}`,
      boxShadow: `0 2px 8px ${colors.shadow}`,
    }),
    transition: "all 0.3s ease",
  },
  title: {
    textAlign: "center",
    marginBottom: screens.xs ? "6px" : "8px",
    color: colors.text, // 暗色模式下使用白色
    fontSize: screens.xs ? "18px" : "22px",
    fontWeight: 600,
    transition: "color 0.3s ease",
  },
  subtitle: {
    textAlign: "center",
    marginBottom: screens.xs ? "12px" : "16px",
    color: colors.textSecondary,
    fontSize: screens.xs ? "12px" : "13px",
    lineHeight: 1.4,
    transition: "color 0.3s ease",
  },
  cardWrapper: ({ isSelected, color, isDark }) => ({
    position: 'relative' as const,
    marginBottom: 0,
    transition: "all 0.3s ease",
    cursor: "pointer",
    minHeight: screens.xs ? "88px" : "96px",
    height: "100%",
  }),
  cardOuterBorder: ({ isSelected, color, isDark }) => {
    // 外部div - 保持原样，不受选中状态影响，但添加过渡效果
    // 亮色模式：外部div，白色背景，1px #73E6FF边框
    if (!isDark) {
      return {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "12px",
        background: "#FFFFFF", // 白色背景
        border: "1px solid #73E6FF", // #73E6FF边框
        zIndex: 1,
        boxShadow: `0 2px 8px ${colors.shadow}`,
        transition: "box-shadow 0.3s ease, transform 0.3s ease", // 添加过渡效果
      };
    }
    // 暗色模式：外部div，保持原来的边框颜色
    return {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: "12px",
      background: colors.messageBg, // 暗色模式背景
      border: `1px solid ${colors.border}`, // 保持原来的边框颜色
      zIndex: 1,
      boxShadow: `0 2px 8px ${colors.shadow}`,
      transition: "box-shadow 0.3s ease, transform 0.3s ease", // 添加过渡效果
    };
  },
  cardInnerBorder: ({ isSelected, color, isDark }) => {
    // 内部div - 向上和右偏移，保持原样，不受选中状态影响，但添加过渡效果
    // 亮色模式：内部div，渐变色背景，向上和右偏移
    if (!isDark) {
      return {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "12px",
        background: "linear-gradient(90deg, #A7FFFA 0%, #F6CFFF 50%, #FFCDF2 100%)", // 渐变色
        transform: "translate(3px, -3px)", // 向上和右偏移
        zIndex: 2,
        transition: "transform 0.3s ease", // 添加过渡效果
      };
    }
    // 暗色模式：内部div，使用#AEFBFB背景，向上和右偏移
    return {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: "12px",
      background: "#AEFBFB", // 暗色模式内部背景色
      transform: "translate(3px, -3px)", // 向上和右偏移
      zIndex: 2,
      transition: "transform 0.3s ease", // 添加过渡效果
    };
  },
  categoryCard: ({ isSelected, color }) => {
    return {
      cursor: "pointer",
      transition: "all 0.3s ease, transform 0.15s ease", // 添加 transform 过渡，让点击反馈更流畅
      height: "100%",
      minHeight: screens.xs ? "88px" : "96px",
      borderRadius: "12px",
      background: colors.messageBg,
      // 选中状态：不添加边框，边框由外部的错位div实现
      // 未选中状态：边框由错位的div实现
      border: 'none', // 边框由错位的div实现
      boxShadow: 'none', // 阴影由错位的div实现
      position: 'relative' as const,
      zIndex: 3, // 内容卡片在最上层
      // 确保 transform 可以正常工作
      willChange: 'transform', // 优化动画性能
    };
  },
  cardBody: (xs: boolean) => ({
    padding: xs ? "10px 8px" : "12px 10px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    textAlign: "left",
    gap: xs ? "4px" : "6px",
    borderRadius: "12px",
  }),
  icon: (xs: boolean, color: string) => ({
    fontSize: xs ? "22px" : "24px",
    color,
    marginBottom: 0,
    marginRight: 0,
    transition: "all 0.3s ease",
    flexShrink: 0,
  }),
  cardTitle: (xs: boolean, isSelected: boolean, color: string) => ({
    margin: 0,
    color: isSelected ? color : colors.text,
    fontSize: xs ? "13px" : "14px",
    fontWeight: 600,
    textAlign: "left",
    lineHeight: 1.3,
    transition: "color 0.3s ease",
  }),
  cardDesc: (xs: boolean) => ({
    color: colors.textSecondary,
    fontSize: xs ? "11px" : "12px",
    lineHeight: 1.35,
    marginBottom: 0,
    textAlign: "left",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    transition: "color 0.3s ease",
  }),
});


